import { Component, EventEmitter, Inject, Output } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { SharedDeliveryLinesBaseComponent } from '@shared/src/components/route/delivery/lines/shared.delivery.lines.base.component';
import { OpenRouteCMRController } from '@shared/src/controllers/open/openroutecmr.controller';
import { RouteCMRController } from '@shared/src/controllers/route/cmr/routecmr.controller';
import { ActionDto } from '@shared/src/dtos/maintenance/action/actionDto';
import { ActionsStripDto } from '@shared/src/dtos/maintenance/action/ActionsStripDto';
import { ECMRDeliveryLineDto } from '@shared/src/dtos/route/cmr/ecmrparts/ECMRDeliveryLineDto';
import { Route_eCMRDeliveryLinesDto } from '@shared/src/dtos/route/cmr/ecmrparts/Route_eCMRDeliveryLinesDto';
import { PalletTypes } from '@shared/src/enums/PalletTypes';
import { ShipmentDeliveryTotalFieldTypes } from '@shared/src/enums/ShipmentDeliveryTotalFieldTypes';

/***
 * Línies d'albarà d'un CMR: la realitat executada a la càrrega. Comparteix
 * tota la lògica d'edició amb l'albarà del shipment a través de
 * SharedDeliveryLinesBaseComponent; aquí només hi queda el que és propi del
 * diàleg (obrir/tancar) i el desat contra updatedeliverylines.
 */
@Component({
  selector: 'updatedeliverylinesdialog-dialog',
  templateUrl: './updatedeliverylinesdialog.dialog.component.html',
  styleUrls: ['./updatedeliverylinesdialog.dialog.component.scss']
})
export class UpdateDeliveryLinesDialogDialogComponent extends SharedDeliveryLinesBaseComponent<ECMRDeliveryLineDto> {

  @Output() public onResolve: EventEmitter<boolean> = new EventEmitter<boolean>();

  public actions: ActionsStripDto;
  public readonly totalFieldTypes = ShipmentDeliveryTotalFieldTypes;

  /***
   * Tots els totals editables del CMR (no té price). Mateix ordre que
   * primaryTotalFields a SharedShipmentDeliveryComponent (llevat de price,
   * que no aplica aquí): kg sempre al final.
   */
  public readonly totalFields: ShipmentDeliveryTotalFieldTypes[] = [
    ShipmentDeliveryTotalFieldTypes.quantity,
    ShipmentDeliveryTotalFieldTypes.boxes,
    ShipmentDeliveryTotalFieldTypes.bags,
    ShipmentDeliveryTotalFieldTypes.sacks,
    ShipmentDeliveryTotalFieldTypes.litres,
    ShipmentDeliveryTotalFieldTypes.m3,
    ShipmentDeliveryTotalFieldTypes.pallet,
    ShipmentDeliveryTotalFieldTypes.netKg,
    ShipmentDeliveryTotalFieldTypes.kg,
  ];

  constructor(@Inject(MAT_DIALOG_DATA) public data: any,
    private mdDialogRef: MatDialogRef<UpdateDeliveryLinesDialogDialogComponent>,
    private mdDialog: MatDialog,
    private ecmrController: RouteCMRController,
    private openEcmrController: OpenRouteCMRController) {
    super();

    // El diàleg sempre s'obre per editar
    this.editMode = true;
    this.loadLines(data != null && data.route_eCMR != null && data.route_eCMR.deliveryLines != null
      ? data.route_eCMR.deliveryLines : []);
    this.resetTotalsState();

    this.actions = new ActionsStripDto();
    this.actions.add(ActionDto.buildAdd("ADD", "ECMR.ADDLINE.CAPTION", "ECMR.ADDLINE.HELP", "fa-plus", 0, 0, 1, ""));
  }

  /***
   * Camps que aquest diàleg (línies executades del CMR) sap pintar. Un camp
   * que existeixi a ECMRDeliveryLineDto però no sigui en aquesta llista
   * queda amagat SEMPRE, sense error (veure el comentari gran a
   * DeliveryLineColumns.ts): si afegeixes una propietat nova i no surt
   * aquí, revisa primer aquesta llista.
   */
  protected supportedColumns(): string[] {
    return ['reference', 'description', 'quantity', 'kg', 'netKg', 'boxes', 'bags', 'sacks', 'litres', 'wDimension',
      'yDimension', 'hDimension', 'm3', 'palletCount', 'palletType', 'publicTags', 'batchInfo'];
  }

  protected buildEmptyLine(): ECMRDeliveryLineDto {
    let line = new ECMRDeliveryLineDto();
    line.line = this.lines.length + 1;
    line.order = line.line + "";
    line.quantity = 0;
    line.kg = 0;
    line.netKg = 0;
    line.wDimension = 0;
    line.yDimension = 0;
    line.hDimension = 0;
    line.m3 = 0;
    line.palletCount = 0;
    line.palletType = PalletTypes.None;
    line.litres = 0;
    line.bags = 0;
    line.boxes = 0;
    line.sacks = 0;
    return line;
  }


  /*** Reindexa order després d'esborrar, com feia el diàleg abans */
  public onDeleteLine(line: ECMRDeliveryLineDto) {
    super.onDeleteLine(line);
    const sorted = this.lines.slice().sort((a, b) => a.line - b.line);
    for (let i = 0; i < sorted.length; i++)
      sorted[i].order = sorted[i].line + "";
  }

  // ///////////////////////////////////////// Totals: valor directe, sense "fixar"
  //
  // A diferència de SharedShipmentDeliveryComponent (on un total és per
  // defecte la suma de línies, i "Fijar" és l'excepció), al CMR els totals
  // sempre s'editen directament: no hi ha concepte de suma en viu. Per això
  // reaprofitem l'emmagatzematge de "fixat" de SharedDeliveryLinesBaseComponent
  // marcant TOTS els camps com a tocats des del principi (getIsFixed() sempre
  // true), de manera que getFixedValue()/onFixedValueChanged() actuen com un
  // simple valor editable per camp, i getTotalValueToSend() no cau mai al
  // càlcul per suma. Cada cop que s'obre el diàleg es parteix del valor ja
  // guardat a route_eCMR (veure constructor i reloadAfterCancel()).

  private resetTotalsState() {
    const route_eCMR = this.data != null ? this.data.route_eCMR : null;
    this.initTotalsState({
      quantity: true, kg: true, netKg: true, boxes: true, bags: true, sacks: true, litres: true, m3: true, pallet: true,
    }, {
      quantity: route_eCMR != null ? route_eCMR.totalQuantity : null,
      kg: route_eCMR != null ? route_eCMR.totalKg : null,
      netKg: route_eCMR != null ? route_eCMR.totalNetKg : null,
      boxes: route_eCMR != null ? route_eCMR.totalBoxes : null,
      bags: route_eCMR != null ? route_eCMR.totalBags : null,
      sacks: route_eCMR != null ? route_eCMR.totalSacks : null,
      litres: route_eCMR != null ? route_eCMR.totalLitres : null,
      m3: route_eCMR != null ? route_eCMR.totalM3 : null,
      pallet: route_eCMR != null ? route_eCMR.totalPalletCount : null,
    });
  }

  /*** Etiqueta del total (reaprofitem les de la columna de línia i les de l'albarà, ja traduïdes) */
  public getTotalCaption(field: ShipmentDeliveryTotalFieldTypes): string {
    switch (field) {
      case ShipmentDeliveryTotalFieldTypes.quantity: return 'SHIPMENTDELIVERY.TOTALQUANTITY.CAPTION';
      case ShipmentDeliveryTotalFieldTypes.kg: return 'ECMR.TOTALKG.CAPTION';
      case ShipmentDeliveryTotalFieldTypes.netKg: return 'ECMR.TOTALNETKG.CAPTION';
      case ShipmentDeliveryTotalFieldTypes.boxes: return 'DELIVERYLINE.BOXES.CAPTION';
      case ShipmentDeliveryTotalFieldTypes.bags: return 'DELIVERYLINE.BAGS.CAPTION';
      case ShipmentDeliveryTotalFieldTypes.sacks: return 'DELIVERYLINE.SACKS.CAPTION';
      case ShipmentDeliveryTotalFieldTypes.litres: return 'DELIVERYLINE.LITRES.CAPTION';
      case ShipmentDeliveryTotalFieldTypes.m3: return 'SHIPMENTDELIVERY.TOTALM3.CAPTION';
      case ShipmentDeliveryTotalFieldTypes.pallet: return 'SHIPMENTDELIVERY.TOTALPALLET.CAPTION';
    }
  }

  protected persist(lines: ECMRDeliveryLineDto[]): Observable<any> {
    const dataToSend: Route_eCMRDeliveryLinesDto = Route_eCMRDeliveryLinesDto.build(lines);
    dataToSend.totalQuantity = this.getTotalValueToSend(this.totalFieldTypes.quantity);
    dataToSend.totalKg = this.getTotalValueToSend(this.totalFieldTypes.kg);
    dataToSend.totalNetKg = this.getTotalValueToSend(this.totalFieldTypes.netKg);
    dataToSend.totalM3 = this.getTotalValueToSend(this.totalFieldTypes.m3);
    dataToSend.totalPalletCount = this.getTotalValueToSend(this.totalFieldTypes.pallet);
    dataToSend.totalBoxes = this.getTotalValueToSend(this.totalFieldTypes.boxes);
    dataToSend.totalBags = this.getTotalValueToSend(this.totalFieldTypes.bags);
    dataToSend.totalSacks = this.getTotalValueToSend(this.totalFieldTypes.sacks);
    dataToSend.totalLitres = this.getTotalValueToSend(this.totalFieldTypes.litres);

    const request = !this.data.external
      ? this.ecmrController.updateDeliveryLines(this.data.routePartCMRId, dataToSend)
      : this.openEcmrController.updateDeliveryLines(this.data.routePartCMRId, dataToSend);

    return request.pipe(map(result => {
      if (result) {
        this.onResolve.next(true);
        this.mdDialog.closeAll();
      }
      return result;
    }));
  }

  protected reloadAfterCancel(): void {
    this.loadLines(this.data != null && this.data.route_eCMR != null && this.data.route_eCMR.deliveryLines != null
      ? this.data.route_eCMR.deliveryLines : []);
    this.resetTotalsState();
  }

  public onClickAction(item: ActionDto) {
    if (item != null && item.actionId == "ADD")
      this.onAddLine();
  }

  public onClose() {
    this.onResolve.next(false);
    this.mdDialog.closeAll();
  }

  /*** El diàleg confirma sempre, encara que no hi hagi canvis marcats */
  public onConfirmDialog() {
    this.persist(this.lines).subscribe();
  }
}
